import { useEffect, useMemo, useRef, useState } from 'react';
import { useLazyLoadQuery, useSubscription } from 'react-relay';
import { getLoggerSafe } from '@vitvio/logging';
import type { GraphQLSubscriptionConfig, GraphQLTaggedNode, OperationType } from 'relay-runtime';

const DEFAULT_POLLING_INTERVAL = 3000;
const DEFAULT_QUERY_KEY = 'query';

interface UseQueryAndSubscriptionOptions<TQuery extends OperationType, TSubscription extends OperationType> {
  pollingInterval?: number;
  queryKey?: string;
  queryVariables?: TQuery['variables'];
  subscriptionVariables?: TSubscription['variables'];
}

export const useQueryAndSubscriptionWithPollingFallback = <
  TQuery extends OperationType,
  TSubscription extends OperationType,
>(
  query: GraphQLTaggedNode,
  subscription: GraphQLTaggedNode,
  options?: UseQueryAndSubscriptionOptions<TQuery, TSubscription>,
): TQuery['response'] => {
  const logger = getLoggerSafe();
  const {
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    queryKey = DEFAULT_QUERY_KEY,
    subscriptionVariables = {},
    queryVariables = {},
  } = options || {};
  const [pollingFallBackEnabled, setPollingFallBackEnabled] = useState(false);
  const [fetchKeyIndex, setFetchKeyIndex] = useState(0);

  const subscriptionConfig: GraphQLSubscriptionConfig<TSubscription> = useMemo(
    () => ({
      subscription,
      variables: subscriptionVariables,
      onError: (error) => {
        logger?.debug('Subscription error, falling back to polling', { error });
        setPollingFallBackEnabled(true);
      },
      onNext: (_data) => {
        setPollingFallBackEnabled((fallBackEnabled) => {
          if (fallBackEnabled) {
            logger?.debug('Subscription next, disabling polling');
          }
          return false;
        });
      },
    }),
    [subscription, subscriptionVariables, logger],
  );

  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (pollingFallBackEnabled) {
      pollingRef.current = setInterval(() => {
        setFetchKeyIndex((index) => index + 1);
      }, pollingInterval);
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [pollingFallBackEnabled]);

  useSubscription(subscriptionConfig);

  const data = useLazyLoadQuery<TQuery>(query, queryVariables, {
    fetchKey: `${queryKey}-${fetchKeyIndex}`,
    fetchPolicy: 'store-and-network',
  });

  return data;
};
